import { formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Safely parse and validate date input
 */
export function safeParseDate(dateInput: string | Date | null | undefined): Date | null {
  if (!dateInput) {
    return null;
  }
  
  let date: Date;
  
  try {
    if (typeof dateInput === 'string') {
      // Try to parse ISO string first, then fall back to Date constructor
      date = dateInput.includes('T') ? parseISO(dateInput) : new Date(dateInput);
    } else {
      date = dateInput;
    }
    
    return isValid(date) ? date : null;
  } catch (error) {
    console.error('safeParseDate: Error parsing date:', dateInput, error);
    return null;
  }
}

/**
 * Safely format date as local date and time strings
 */
export function safeFormatDateTime(dateInput: string | Date | null | undefined): {
  dateString: string;
  timeString: string;
  isValid: boolean;
} {
  const date = safeParseDate(dateInput);
  
  if (!date) {
    return {
      dateString: 'Invalid date',
      timeString: 'Invalid time', 
      isValid: false
    };
  }
  
  return {
    dateString: date.toLocaleDateString(),
    timeString: date.toLocaleTimeString(),
    isValid: true
  };
}

/**
 * Safely format relative time using formatDistanceToNow
 */
export function safeFormatRelativeTime(dateInput: string | Date | null | undefined): string {
  const date = safeParseDate(dateInput);
  
  if (!date) {
    return 'Invalid date';
  }
  
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('safeFormatRelativeTime: Error formatting relative time:', dateInput, error);
    return 'Invalid date';
  }
}

/**
 * Complete date formatting with fallback for invalid dates
 */
export function safeFormatFullDateTime(dateInput: string | Date | null | undefined): string {
  const { dateString, timeString, isValid } = safeFormatDateTime(dateInput);
  
  if (!isValid) {
    return 'Invalid date';
  }
  
  const relativeTime = safeFormatRelativeTime(dateInput);
  return `${dateString} at ${timeString} (${relativeTime})`;
}